import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box } from 'rebass';

const AlignStyle = Quill.import('attributors/style/align');
Quill.register(AlignStyle, true);

// const IndentStyle = Quill.import('attributors/style/indent');
// Quill.register(IndentStyle, true);

const modules = {
  toolbar: [
    ['bold', 'italic'],
    // [{ list: 'ordered' }, { list: 'bullet' }],
    // [{ align: '' }, { align: 'center' }, { align: 'right' }],
    [{ header: [1, 2, 3, 4, false] }],
    // [{ color: [] }, { background: [] }],
    // ['link'],
  ],
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  // 'indent',
  'link',
  'image',
  'color',
  'background',
  'blockquote',
  'align',
];

const MarkdownEditor = ({ field: { value, ...field }, style, form: { setFieldValue } }) => {
  const [editorState, setEditorState] = useState(value);
  const ref = useRef(null);
  const boundsRef = useRef(null);

  useEffect(() => {
    if (ref) {
      ref.current.focus();
    }
  }, [ref]);

  // After a paste, land on (and scroll to reveal) the caret at the END of the pasted
  // text — instead of a stale/top-jumped scroll position.
  //
  // Quill 1.x's clipboard reads the pasted content by focusing an OFF-SCREEN capture
  // <div>; the browser reacts by scrolling the nearest scrollable ancestor (here the
  // modal's ShadeInner, `overflow-y: scroll`) to reveal that element — jumping to the
  // top. Quill then re-focuses the real editor and calls its own scrollIntoView, but
  // that's keyed off `scrollingContainer` (defaults to the editor's own root), never
  // the modal body, so the jump is never corrected there.
  //
  // Quill DOES already move the caret to the end of the pasted content (its clipboard
  // handler calls setSelection(insertEnd) once the paste finishes inserting). So once
  // that settles, we just need to scroll every real scrollable ancestor by the minimal
  // amount to reveal wherever the caret actually landed — via each container's own
  // scrollTop math (native element.scrollIntoView can't reach across the off-screen
  // capture <div> Quill uses mid-paste, so we compute it ourselves from the live caret
  // Range, walking innermost ancestor outward so later measurements reflect earlier
  // scroll adjustments).
  useEffect(() => {
    const editor = ref.current && ref.current.getEditor && ref.current.getEditor();
    const root = editor && editor.root;
    if (!root) return undefined;

    const collectScrollers = () => {
      const scrollers = [];
      let node = root.parentElement;
      while (node && node !== document.documentElement) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
          scrollers.push(node);
        }
        node = node.parentElement;
      }
      return scrollers;
    };

    const getCaretRect = () => {
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0 || !root.contains(sel.focusNode)) return null;
      const range = sel.getRangeAt(0).cloneRange();
      range.collapse(false); // end of the (now-inserted) selection
      let rect = range.getBoundingClientRect();
      // Collapsed ranges at a node boundary sometimes report an all-zero rect —
      // fall back to the focus node's element bounds in that case.
      if (rect && rect.width === 0 && rect.height === 0 && rect.top === 0 && rect.left === 0) {
        const el = sel.focusNode.nodeType === 1 ? sel.focusNode : sel.focusNode.parentElement;
        rect = el && el.getBoundingClientRect();
      }
      return rect;
    };

    const revealCaret = () => {
      const scrollers = collectScrollers(); // innermost first
      scrollers.forEach((node) => {
        const rect = getCaretRect();
        if (!rect) return;
        const containerRect = node.getBoundingClientRect();
        if (rect.top < containerRect.top) {
          node.scrollTop -= containerRect.top - rect.top;
        } else if (rect.bottom > containerRect.bottom) {
          node.scrollTop += rect.bottom - containerRect.bottom;
        }
      });
      const rect = getCaretRect();
      if (!rect) return;
      if (rect.top < 0) window.scrollBy(0, rect.top);
      else if (rect.bottom > window.innerHeight) window.scrollBy(0, rect.bottom - window.innerHeight);
    };

    const onPasteCapture = () => {
      // Quill inserts the pasted delta (and moves the caret to the end of it) in its
      // own setTimeout(…, 1); run after that settles, and once more a couple frames
      // later in case layout/reflow from a large paste finishes late.
      setTimeout(revealCaret, 20);
      requestAnimationFrame(() => requestAnimationFrame(revealCaret));
    };

    root.addEventListener('paste', onPasteCapture, true);
    return () => root.removeEventListener('paste', onPasteCapture, true);
  }, []);

  const onChange = useCallback(
    value => {
      setEditorState(value);
      setFieldValue(field.name, value.toString('html'));
    },
    [field.name, setFieldValue]
  );

  return (
    <Box id="quill_bounds" style={style}>
      <ReactQuill
        className="ql-lifetales"
        ref={ref}
        formats={formats}
        modules={modules}
        placeholder={`Write about the moment. It can be as simple as expressing your thoughts or feelings to your baby, or a detailed description of a memory. `}
        theme="snow"
        onChange={onChange}
        value={editorState}
        bounds="#quill_bounds"
      />
    </Box>
  );
};

MarkdownEditor.propTypes = {
  field: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
};

export default MarkdownEditor;
